'use client';

import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';

import GroupTabs from '@/containers/OurLeadership/GroupTabs';
import GroupBoard from '@/containers/OurLeadership/GroupBoard';
import GroupCompanies from '@/containers/OurLeadership/GroupCompanies';
import { Modal } from '@/components/Modal/Modal';
import LeaderDetailModal from '@/containers/OurLeadership/LeaderDetailModal';
import GroupSelect from '@/containers/OurLeadership/GroupSelect';
import { sortPeople } from '@/containers/OurLeadership/utils';
import './LeadershipTabs.scss';

import { TypeOurPeopleWithoutUnresolvableLinksResponse } from '@/features/contentful/_generated';

const OurLeadershipPage = ({
    peoples,
    isModalOpen,
    setIsModalOpen,
}: {
    peoples: TypeOurPeopleWithoutUnresolvableLinksResponse[];
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
    const { items: livePeople } = useContentfulLiveUpdates({ items: peoples });
    const [active, setActive] = useState(true);
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const { push } = useRouter();

    const [selectedEntry, setSelectedEntry] = useState<
        TypeOurPeopleWithoutUnresolvableLinksResponse | undefined
    >(() =>
        searchParams.has('id')
            ? livePeople.find((item) => item.sys.id === searchParams.get('id'))
            : undefined,
    );

    const handleModalOpen = useCallback(
        (entityId: string) => {
            const params = new URLSearchParams(searchParams);
            params.set('id', entityId);
            setIsModalOpen(true);
            setSelectedEntry(livePeople.find((item) => item.sys.id === entityId));

            push(`${pathname}?${params.toString()}`, { scroll: false });
        },
        [pathname, livePeople, push, setIsModalOpen, searchParams],
    );

    const handleModalClose = useCallback(() => {
        const params = new URLSearchParams(searchParams);
        params.delete('id');
        setIsModalOpen(false);

        push(`${pathname}?${params.toString()}`, { scroll: false });
    }, [pathname, push, setIsModalOpen, searchParams]);

    useEffect(() => {
        const closeOnEscapePressed = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleModalClose();
            }
        };
        window.addEventListener('keydown', closeOnEscapePressed);
        return () => window.removeEventListener('keydown', closeOnEscapePressed);
    }, [handleModalClose]);

    const toggleTab = () => {
        setActive((prevState) => !prevState);
    };

    const members = useMemo(() => {
        return sortPeople(livePeople);
    }, [livePeople]);

    useEffect(() => {
        setSelectedEntry((prevState) =>
            livePeople.find((item) => item.sys.id === prevState?.sys.id),
        );
    }, [livePeople]);

    return (
        <div>
            <div className=" tw-mt-[24px] tw-flex tw-h-[100px] tw-w-full tw-items-start tw-justify-center">
                <GroupTabs onTabClick={toggleTab} />
                <GroupSelect onOptionClick={toggleTab} />
            </div>
            <Modal
                isOpen={isModalOpen}
                handleClose={handleModalClose}
                classes={{
                    root: 'mobile tw-min-h-screen tw-w-full tw-bg-white md:tw-w-full md:tw-min-h-0',
                }}
            >
                {selectedEntry && <LeaderDetailModal leader={selectedEntry} />}
            </Modal>
            {active ? (
                <GroupBoard
                    handleModalOpen={handleModalOpen}
                    peoples={members.filter((a) => !!a.fields.boardMember)}
                    hidePreview={isModalOpen}
                />
            ) : (
                <GroupCompanies
                    handleModalOpen={handleModalOpen}
                    peoples={members.filter((a) => !a.fields.boardMember)}
                    hidePreview={isModalOpen}
                />
            )}
        </div>
    );
};

export default OurLeadershipPage;
